// Warm-start caches for the retail POS.
//
// Opening the POS used to fetch the item catalog from `/shop/items` every single
// time — up to 250 items, each carrying its alternate-unit array — and it did it
// twice per open (the till screen's warm-up plus the cart grid's own first page).
// The route is cached server-side, but the browser still paid the round trip and
// the JSON parse before the cashier could see a single product, and nothing
// survived closing the POS, reloading the page or rebooting the till.
//
// What is cached here:
//   catalog   — the browsable/searched item list. localStorage, because "open the
//               POS in the morning" must be instant even after a restart.
//   bootstrap — customers/warehouses/taxes/accounts/open invoices for the till.
//               sessionStorage, because it is a heavier payload that a running
//               session refreshes on its own.
//
// Both entries are versioned (a shape change can never be read back as garbage)
// and scoped to the signed-in user, so one cashier's till never shows another
// account's catalog.
//
// Reading is cheap: the parsed entry is memoised per session, so a multi-hundred
// kilobyte JSON is not re-parsed every time a panel mounts.

export const POS_CATALOG_CACHE_KEY = 'dira360:retail:pos-catalog:v1';
export const POS_BOOTSTRAP_CACHE_KEY = 'dira360:retail:pos-bootstrap:v1';

/** Items requested for the warm-up fetch that fills the catalog. */
export const POS_CATALOG_LIMIT = 250;

/** While a catalog is this fresh the POS will not hit the network at all. */
export const POS_CATALOG_TTL_MS = 2 * 60 * 1000;
/** Past this age the catalog is dropped — a till must never sell from yesterday's data. */
export const POS_CATALOG_MAX_AGE_MS = 12 * 60 * 60 * 1000;
/** The till bootstrap lives a little longer than the catalog, in session storage. */
export const POS_BOOTSTRAP_TTL_MS = 5 * 60 * 1000;
export const POS_BOOTSTRAP_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/** Refuse to persist anything larger than this (localStorage quota is ~5 MB). */
const POS_CATALOG_MAX_BYTES = 2.5 * 1024 * 1024;
const POS_BOOTSTRAP_MAX_BYTES = 3.5 * 1024 * 1024;

let catalogMemory = null;
let bootstrapMemory = null;

const storage = (kind) => {
  if (typeof window === 'undefined') return null;
  try {
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch (_) {
    return null;
  }
};

const storeGet = (kind, key) => {
  const store = storage(kind);
  if (!store) return null;
  try { return store.getItem(key); } catch (_) { return null; }
};

const storeSet = (kind, key, value) => {
  const store = storage(kind);
  if (!store) return null;
  try { store.setItem(key, value); return true; } catch (_) { return false; }
};

const storeRemove = (kind, key) => {
  const store = storage(kind);
  if (!store) return;
  try { store.removeItem(key); } catch (_) { /* ignore */ }
};

const isItemArray = (value) => (
  Array.isArray(value) && value.every((row) => row && typeof row === 'object' && row.id)
);

const scopeOf = (value) => String(value || 'anonymous');

/** A parsed, still-usable entry, or null (dropping anything expired or foreign). */
const readEntry = (kind, key, { scope, maxAgeMs, validate }) => {
  const raw = storeGet(kind, key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const at = Number(parsed?.at || 0);
    if (!(at > 0) || (Date.now() - at) > maxAgeMs) {
      storeRemove(kind, key);
      return null;
    }
    // A cache written for another signed-in user is discarded, not shown.
    if (scopeOf(parsed?.scope) !== scopeOf(scope)) return null;
    if (!validate(parsed)) {
      storeRemove(kind, key);
      return null;
    }
    return parsed;
  } catch (_) {
    storeRemove(kind, key);
    return null;
  }
};

const writeEntry = (kind, key, maxBytes, entry) => {
  let payload = '';
  try { payload = JSON.stringify(entry); } catch (_) { return false; }
  if (payload.length > maxBytes) {
    // Too big to keep on disk; the in-memory copy still speeds up this session.
    storeRemove(kind, key);
    return false;
  }
  return storeSet(kind, key, payload) === true;
};

// ── Item catalog ──────────────────────────────────────────────────────────────

/**
 * The last catalog, if it is still usable. Returns `{ at, items, total }` or null.
 * A result older than POS_CATALOG_TTL_MS is still returned — the caller paints it
 * immediately and revalidates in the background.
 */
export function readPosCatalogCache(scope) {
  if (catalogMemory) {
    return scopeOf(catalogMemory.scope) === scopeOf(scope) ? catalogMemory : null;
  }
  const parsed = readEntry('local', POS_CATALOG_CACHE_KEY, {
    scope,
    maxAgeMs: POS_CATALOG_MAX_AGE_MS,
    validate: (entry) => isItemArray(entry?.items) && entry.items.length > 0
  });
  if (!parsed) return null;
  catalogMemory = {
    at: Number(parsed.at),
    items: parsed.items,
    total: Number(parsed.total || parsed.items.length),
    scope: scopeOf(parsed.scope)
  };
  return catalogMemory;
}

/** True when the catalog is fresh enough to skip the network entirely. */
export function isPosCatalogFresh(entry, now = Date.now()) {
  return Boolean(entry) && (now - Number(entry.at || 0)) <= POS_CATALOG_TTL_MS;
}

export function writePosCatalogCache(items, total, scope) {
  const rows = Array.isArray(items) ? items.filter((row) => row && typeof row === 'object' && row.id) : [];
  if (!rows.length) return null;
  const entry = {
    at: Date.now(),
    scope: scopeOf(scope),
    items: rows,
    total: Number(total || rows.length)
  };
  catalogMemory = entry;
  writeEntry('local', POS_CATALOG_CACHE_KEY, POS_CATALOG_MAX_BYTES, entry);
  return entry;
}

export function clearPosCatalogCache() {
  catalogMemory = null;
  storeRemove('local', POS_CATALOG_CACHE_KEY);
}

// ── Till bootstrap ────────────────────────────────────────────────────────────

export function readPosBootstrapCache(scope) {
  if (bootstrapMemory) {
    return scopeOf(bootstrapMemory.scope) === scopeOf(scope) ? bootstrapMemory : null;
  }
  const parsed = readEntry('session', POS_BOOTSTRAP_CACHE_KEY, {
    scope,
    maxAgeMs: POS_BOOTSTRAP_MAX_AGE_MS,
    validate: (entry) => Boolean(entry?.payload) && typeof entry.payload === 'object'
  });
  if (!parsed) return null;
  bootstrapMemory = { at: Number(parsed.at), scope: scopeOf(parsed.scope), payload: parsed.payload };
  return bootstrapMemory;
}

export function isPosBootstrapFresh(entry, now = Date.now()) {
  return Boolean(entry) && (now - Number(entry.at || 0)) <= POS_BOOTSTRAP_TTL_MS;
}

export function writePosBootstrapCache(payload, scope) {
  if (!payload || typeof payload !== 'object') return null;
  const entry = { at: Date.now(), scope: scopeOf(scope), payload };
  bootstrapMemory = entry;
  writeEntry('session', POS_BOOTSTRAP_CACHE_KEY, POS_BOOTSTRAP_MAX_BYTES, entry);
  return entry;
}

export function clearPosBootstrapCache() {
  bootstrapMemory = null;
  storeRemove('session', POS_BOOTSTRAP_CACHE_KEY);
}

/** Forget both caches — used on sign-out so a till never leaks data across users. */
export function clearPosCaches() {
  clearPosCatalogCache();
  clearPosBootstrapCache();
}

/** Test seam: drop the parsed copies without touching storage. */
export function resetPosCacheMemory() {
  catalogMemory = null;
  bootstrapMemory = null;
}
